// The account's access code: folded till asked for, hidden till shown — then with its QR code, which a
// phone's camera opens — copied; and a field for another device's code, whose account this browser
// takes.
import { useMemo, useRef, useState } from "preact/hooks";
import { signInWith } from "../account.ts";
import { qr } from "../qr.ts";
import { show, state } from "../store.ts";

const HIDDEN = "••••-••••-••••-••••-••••-••••-••••";
/** This machine's own names, which no other device opens. */
export const LOCALHOST = /^(localhost|127\.|\[::1\])/;

export function Access() {
  const [shown, setShown] = useState(false);
  const other = useRef<HTMLInputElement>(null);
  const local = LOCALHOST.test(location.hostname);
  // What the QR code carries: the page with the code after `#`, which never goes to the server; on
  // localhost, which a phone cannot open, the code alone.
  const carried = local ? state.code : `${location.origin}/#code=${state.code}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.code);
      show("код доступа скопирован");
    } catch {
      show("не скопировалось: покажите код и скопируйте его сами", true);
    }
  };
  const use = () => void signInWith(other.current?.value ?? "");
  return (
    <details class="access">
      <summary>Код доступа</summary>
      <p class="hint">
        Аккаунт без почты и телефона: этот код открывает его на другом устройстве — с пространствами и правами
        владельца. Не показывайте его другим.
      </p>
      <div class="row">
        <code class="access-code">{shown ? state.code : HIDDEN}</code>
        <button onClick={() => setShown(!shown)}>{shown ? "Скрыть" : "Показать"}</button>
        <button onClick={copy}>Скопировать</button>
      </div>
      {shown && (
        <div class="qr">
          <QrCode text={carried} />
        </div>
      )}
      {local && <p class="hint">Телефон не откроет localhost: откройте страницу по адресу этой машины в сети.</p>}
      <div class="row">
        <input
          ref={other}
          placeholder="Код с другого устройства"
          autocapitalize="characters"
          autocomplete="off"
          spellcheck={false}
          onKeyDown={(e) => e.key === "Enter" && use()}
        />
        <button onClick={use}>Войти</button>
      </div>
    </details>
  );
}

/** The QR code of `text`: a quiet zone of four modules round it, four pixels a module. */
export function QrCode({ text }: { text: string }) {
  const rows = useMemo(() => qr(text), [text]);
  if (rows === null) return null;
  const n = rows.length + 8;
  const path = rows.flatMap((row, y) => row.map((dark, x) => (dark ? `M${x + 4} ${y + 4}h1v1h-1z` : ""))).join("");
  return (
    <svg viewBox={`0 0 ${n} ${n}`} width={n * 4} height={n * 4}>
      <rect width={n} height={n} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}
